<template>
  <VInput
    v-if="elementType === 'text'"
    :value="_getValue"
    :errors="getErrors"
    :placeholder="placeholder"
    :classType="classType"
    :name="name"
    :icon="icon"
    :label="label"
    :type="type"
    :layout="layout"
    :hideLabel="hideLabel"
    :disabled="disabled"
    :extraClass="extraClass"
    :hint="hint"
    :required="isRequired"
    :innerChildProps="innerChildProps"
    @input="onChange"
    @blur="onBlur"
    @focus="() => updateMeta(name, 'focused', true, formName)"
    @focusin="() => _updateMeta('isFocused', true)"
    @focusout="() => _updateMeta('isFocused', false)"
  ></VInput>

  <CreditCardInput
    v-else-if="elementType === 'creditCard'"
    :value="_getValue"
    :errors="getErrors"
    :placeholder="placeholder"
    :classType="classType"
    :name="name"
    :label="label"
    :disabled="disabled"
    :extraClass="extraClass"
    @input="onChange"
  >
  </CreditCardInput>

  <VTelInput
    v-else-if="elementType === 'telInput'"
    :value="_getValue"
    :errors="getErrors"
    :placeholder="placeholder"
    :classType="classType"
    :name="name"
    :icon="icon"
    :label="label"
    :type="type"
    :layout="layout"
    :hideLabel="hideLabel"
    :disabled="disabled"
    :extraClass="extraClass"
    :hint="hint"
    :required="isRequired"
    @input="onChange"
    @blur="onBlur"
    @focus="() => updateMeta(name, 'focused', true, formName)"
    @focusin="() => _updateMeta('isFocused', true)"
    @focusout="() => _updateMeta('isFocused', false)"
    @innerValidation="message => _updateMeta('errors', message)"
  >
  </VTelInput>

  <VSelect
    v-else-if="elementType === 'select'"
    :options="selectItems"
    :value="_getValue"
    :errors="getErrors"
    :classType="classType"
    :label="label"
    :name="name"
    :layout="layout"
    :hideLabel="hideLabel"
    :placeholder="placeholder"
    :disabled="disabled"
    :extraClass="extraClass"
    :extraClassRoot="extraClassRoot"
    :position="position"
    :withFilter="withFilter"
    :required="isRequired"
    @select="(e, opt) => onChange(opt.value)"
  >
  </VSelect>

  <VSelectList
    v-else-if="elementType === 'tags'"
    :classType="classType"
    :selectedItems="_getValue"
    :multiple="multiple"
    :name="name"
    :label="label"
    :items="selectItems"
    :errors="getErrors"
    :onAddHandler="onAddHandler"
    :onRemoveHandler="onRemoveHandler"
    :disabled="disabled"
    :isLoading="isLoading"
    :withDelete="withDelete"
    :extraClass="extraClass"
    :maxInputLength="maxLength"
    :onSearchAsyncHandler="onSearchAsyncHandler"
    @error="synchronizeErrors"
    @select="onChange"
    @focus="() => updateMeta(name, 'focused', true, formName)"
    @blur="onBlur"
  >
  </VSelectList>

  <VVueSelect
    v-else-if="elementType === 'vue-select'"
    :options="selectItems"
    :value="_getValue"
    :errors="getErrors"
    :classType="classType"
    :label="label"
    :name="name"
    :onSearch="onSearchAsyncHandler"
    :layout="layout"
    :hideLabel="hideLabel"
    :placeholder="placeholder"
    :disabled="disabled"
    :extraClass="extraClass"
    :extraClassRoot="extraClassRoot"
    :position="position"
    :withFilter="withFilter"
    :required="isRequired"
    @select="opt => onChange(opt.value)"
  >
  </VVueSelect>

  <VTagsCreator
    v-else-if="elementType === 'tagsCreator'"
    :classType="classType"
    :value="_getValue"
    :name="name"
    :label="label"
    :errors="getErrors"
    :valueValidationFunc="valueValidationFunc"
    :extraClass="extraClass"
    :version="version"
    :placeholder="placeholder"
    :preventEllipsis="preventEllipsis"
    :minValuesCount="minValuesCount"
    @change="onChange"
  >
  </VTagsCreator>

  <VCheckbox
    v-else-if="elementType === 'checkbox'"
    :classType="classType"
    :name="name"
    :label="label"
    :checked="_getValue"
    :extraClass="extraClass"
    :disabled="disabled"
    :hint="hint"
    :connectedWith="connectedWith"
    @change="onChange"
    :innerChildProps="innerChildProps"
  ></VCheckbox>

  <VCustomDataCreator
    v-else-if="elementType === 'customData'"
    :classType="classType"
    :name="name"
    :label="label"
    :customData="_getValue"
    :errors="getErrors"
    :extraClass="extraClass"
    @apply="onChange"
    @remove="onChange"
  >
  </VCustomDataCreator>

  <VTextArea
    v-else-if="elementType === 'textarea'"
    :classType="classType"
    :name="name"
    :label="label"
    :errors="getErrors"
    :value="_getValue"
    :placeholder="placeholder"
    :extraClass="extraClass"
    :required="isRequired"
    @change="onChange"
  >
  </VTextArea>

  <VPrismCode
    v-else-if="elementType === 'code'"
    :classType="classType"
    :name="name"
    :label="label"
    :subLabel="subLabel"
    :additionalText="additionalText"
    :errors="getErrors"
    :code="_getValue"
    :placeholder="placeholder"
    :extraClass="extraClass"
    :innerChildProps="innerChildProps"
    @change="onChange"
    @blur="onBlur"
  >
  </VPrismCode>

  <VRadioInputGroup
    v-else-if="elementType === 'radioGroup'"
    :name="name"
    :label="label"
    :checked="_getValue"
    :items="selectItems"
    :extraClass="extraClass"
    @change="onChange"
  >
  </VRadioInputGroup>

  <VRangeIntInput
    v-else-if="elementType === 'rangeInt'"
    :name="name"
    :label="label"
    :firstFixed="firstFixed"
    :lastFixed="lastFixed"
    :value="_getValue"
    @change="onChange"
  ></VRangeIntInput>

  <VSwitcher
    v-else-if="elementType === 'switcher'"
    :name="name"
    :type="switcherType"
    :activeKey="_getValue"
    :switcherKeys="switcherKeys"
    :extraClass="extraClass"
    :onClickHandler="onChange"
  >
  </VSwitcher>

  <VMultipleElementsCreator
    v-else-if="elementType === 'multipleElementsCreator'"
    :name="name"
    :formName="formName"
    :label="label"
    :additionalText="additionalText"
    :value="_getValue"
    :extraClass="extraClass"
    :innerChildProps="innerChildProps"
    :required="isRequired"
    @change="onChange"
  >
  </VMultipleElementsCreator>

  <VEditableText
    v-else-if="elementType === 'editableText'"
    :value="_getValue"
    :errors="getErrors"
    :addPlaceholder="placeholder"
    :classType="classType"
    :_key="name"
    :formName="formName"
    :icon="icon"
    :label="label"
    :type="type"
    :hideLabel="hideLabel"
    :disabled="disabled"
    :extraClass="extraClass"
    :required="isRequired"
    :onBlurHandler="onBlur"
    @change="(key, val) => onChange(val)"
    @blur="onBlur"
    @focus="() => updateMeta(name, 'focused', true, formName)"
    @focusin="() => _updateMeta('isFocused', true)"
    @focusout="() => _updateMeta('isFocused', false)"
  ></VEditableText>

  <VSimpleFileUploader
    v-else-if="elementType === 'uploader'"
    :value="_getValue"
    :label="label"
    :type="uploaderType"
    :isLoading="isLoading"
    :names="names"
    :withDelete="withDelete"
    :onAddHandler="onAddHandler"
    :onRemoveHandler="onRemoveHandler"
    :extraClass="extraClass"
    :innerChildProps="innerChildProps"
    @change="onChange"
  ></VSimpleFileUploader>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions } from "vuex";

// components
import VInput from "@/components/VInput";
import CreditCardInput from "@/components/CreditCardInput";
import VSelect from "@/components/VSelect";
import VSelectList from "@/components/VSelectList";
import VVueSelect from "@/components/VVueSelect";
import VCheckbox from "@/components/VCheckbox";
import VCustomDataCreator from "@/components/VCustomDataCreator";
import VRadioInputGroup from "@/components/VRadioInputGroup";
import VTextArea from "@/components/VTextArea";
import VTagsCreator from "@/components/VTagsCreator";
import VPrismCode from "@/components/VPrismCode";
import VRangeIntInput from "@/components/VRangeIntInput";
import VSwitcher from "@/components/VSwitcher";
import VMultipleElementsCreator from "@/components/VMultipleElementsCreator";
import VTelInput from "@/components/VTelInput";
import VEditableText from "@/components/VEditableText";
import VSimpleFileUploader from "@/components/VSimpleFileUploader";

import withFormUtils from "@/mixins/withFormUtils";
import { validate } from "@/utils/validations";

export default Vue.component("VFormField", {
  props: {
    name: VueTypes.string.isRequired,
    elementType: VueTypes.oneOf([
      "text",
      "number",
      "creditCard",
      "select",
      "tags",
      "checkbox",
      "customData",
      "tagsCreator",
      "radioGroup",
      "textarea",
      "code",
      "rangeInt",
      "switcher",
      "multipleElementsCreator",
      "editableText",
      "uploader"
    ]).def("text"),
    validate: VueTypes.oneOfType([
      VueTypes.arrayOf(VueTypes.func),
      VueTypes.func
    ]),
    isLoading: VueTypes.bool,
    placeholder: VueTypes.string,
    label: VueTypes.string,
    subLabel: VueTypes.string,
    additionalText: VueTypes.string,
    classType: VueTypes.oneOf(["primary", "secondary", "tertiary", "dark"]).def(
      "primary"
    ),
    formName: VueTypes.string,
    icon: VueTypes.string,
    layout: VueTypes.oneOf(["half", "full"]).def("full"),
    hideLabel: VueTypes.bool.def(false),
    maxLength: VueTypes.number,
    onRemoveHandler: {
      type: Function,
      default: null
    },
    withDelete: VueTypes.bool,
    // props for inner components
    innerChildProps: VueTypes.object,

    // for select only
    extraClassRoot: VueTypes.string.def(""),
    position: VueTypes.oneOf(["down", "up"]).def("down"),

    // for type select / select list
    selectItems: VueTypes.array,
    withFilter: VueTypes.bool.def(false),

    // for select list (tags)
    multiple: VueTypes.bool.def(false),
    onAddHandler: VueTypes.func,

    // for simple input
    type: VueTypes.oneOf(["text", "password"]).def("text"),
    extraClass: VueTypes.string,
    disabled: VueTypes.bool.def(false),

    errorsOnTouched: VueTypes.bool.def(false),
    hint: VueTypes.string.def(""),

    // for type tagsCreator
    valueValidationFunc: VueTypes.func,
    preventEllipsis: VueTypes.bool.def(false),
    version: VueTypes.oneOf(["old", "new"]).def("old"),
    minValuesCount: VueTypes.number.def(0),

    //for rangeInt
    firstFixed: VueTypes.bool.def(true),
    lastFixed: VueTypes.bool.def(false),

    // for switcher
    switcherType: VueTypes.oneOf(["element", "toggle", "toggleReverse"]).def(
      "toggle"
    ),
    switcherKeys: VueTypes.object,

    //for checkbox
    connectedWith: VueTypes.string,

    //for editabletext
    onBlurHandler: VueTypes.func,

    //for uploader
    uploaderType: VueTypes.oneOf(["simple", "dragAndDrop"]),
    names: VueTypes.array,

    //for vue-select
    onSearchAsyncHandler: {
      type: Function,
      default: null
    },

    // property to specify if field required explicitly
    // might be used for displaying some visuals for required fields
    isRequired: VueTypes.bool.def(false)
  },

  mixins: [withFormUtils],

  components: {
    VInput,
    CreditCardInput,
    VSelect,
    VSelectList,
    VVueSelect,
    VCheckbox,
    VCustomDataCreator,
    VTextArea,
    VTagsCreator,
    VRadioInputGroup,
    VPrismCode,
    VRangeIntInput,
    VSwitcher,
    VMultipleElementsCreator,
    VTelInput,
    VEditableText,
    VSimpleFileUploader
  },

  computed: {
    _getField() {
      return this.getField(this.name, this.formName);
    },

    _getValue() {
      return this.getValue(this.name, this.formName);
    },

    _touched() {
      const { meta } = this._getField;
      return meta.blurred && meta.focused;
    },

    _isFocused() {
      const { meta } = this._getField;
      return meta.isFocused;
    },

    getErrors() {
      const { meta } = this._getField;
      return !this._isFocused && this._touched && meta.errors
        ? this._getField.meta.errors
        : [];
    }
  },

  methods: {
    _updateMeta(key, value) {
      this.updateMeta(this.name, key, value, this.formName);
    },

    onBlur() {
      this._updateMeta("blurred", true);
      this.$emit("blur");
      // this.validateField({ formName: this.formName, fieldName: this.name });
    },

    onFocusIn() {
      this.updateMeta(this.name, "isFocused", true, this.formName);
    },

    onChange(val) {
      this.updateValue({
        formName: this.formName,
        fieldName: this.name,
        value: val
      });

      this.setFieldTouchedValue(this.name, true, this.formName);

      // validate all fields which touched
      this.validateForm(this.formName, true);

      // this.syncErrors({
      //   formName: this.formName,
      //   fieldName: this.name,
      //   errors: validate(val, this.validate, this.getFormValues(this.formName))
      // });

      this.$emit("change", { name: this.name, value: val });
    },

    synchronizeErrors(errors) {
      this.syncErrors({
        formName: this.formName,
        fieldName: this.name,
        errors: errors
      });
    },

    ...mapActions("forms", ["updateValue", "syncErrors"])
  },

  watch: {
    _touched(isTouched) {
      if (isTouched) {
        // validate all fields which touched
        this.validateForm(this.formName, true);
      }
    }
  }
});
</script>
